import { checkVisibility, toFloat } from "../../utilities";

const getFilteredList = ({ modelValue, dependencies, name }) => {
    const filteredList = modelValue.filter((model) => {
        return checkVisibility({
            item: {
                dependencies,
                name
            },
            model
        });
    });

    return filteredList;
};

const handlers = {
    methods: {
        /* formula to copy a value from another question
            "formula": "copy",
            "arguments": {
                "targets": [
                    "first-name"
                ]                
            }
        */
        copy: function (args, modelData) {
            const { targets = [] } = args;
            const property = targets[0];

            return modelData[property];
        },
        
        /* formula to count the entries in an array such as a data table
            "formula": "count",
            "arguments": {
                "targets": [
                    "employees"
                ],
                "dependencies": {
                    "items": []
                    "operator": "and"                
            }
        */
        count: function (args, modelData) {
            const { targets, dependencies } = args;
            // If argument is an array, will add up all entries and return a total count
            const count = targets.reduce((memo, thisArg) => {
                const modelValue = modelData[thisArg];

                if (!Array.isArray(modelValue)) {
                    throw new Error('arguments in the "count" method must be an array');
                }
                
                if (dependencies) {
                    const filteredList = getFilteredList({
                        modelValue,
                        dependencies,
                        name: thisArg
                    });
                    
                    return memo + filteredList.length;
                }

                return memo + modelValue.length;
            }, 0);

            return count;
        },

        /* formula to adjust a date by a number of days or a preset formula
            "formula": "dateAdjust",
            "arguments": {
                "targets": [
                    "endDate"
                ],
                "preset": "trailingTwelveMonths"
            }
        */
        dateAdjust: function (args, modelData) {
            const { targets, preset } = args;
            const property = targets[0];
            const value = modelData[property];

            if (preset && value) {
                const date = new Date(value);

                switch (preset) {
                    case 'trailingTwelveMonths':
                        const startYear = date.getFullYear();
                        const startMonth = date.getMonth();
                        const startDay = date.getUTCDate();

                        // Handle leap days
                        // It sounds like there are multiple approaches to calculating trailing twelve months with leap days
                        // This approach "ignores" the leap day and treats it as the 28th of February
                        // Note: startMonth is 0-indexed, so February is 1
                        if (startMonth === 1 && startDay === 29) {
                            date.setUTCDate(28);
                        }

                        // Set the date to 365 days ago
                        date.setFullYear(startYear - 1);

                        // Add one day to get to the start of the fiscal year
                        date.setUTCDate(date.getUTCDate() + 1);
                        break;
                    default:
                        break;
                }

                return date.toISOString().split('T')[0];
            }
        },

        /* formula to calculate the difference between multiple fields, including those with an array-type value (data-table)
            "formula": "difference",
            "arguments": {
                "targets": [
                    "employees"
                ],
                "dependencies": { // Only supported for data tables
                    "items": []
                    "operator": "and" 
                },
                "targetField": "salary" // Only supported for data tables               
            }
        */
        difference: function (args, modelData) {
            const { targets, targetField, dependencies, values } = args;

            const targetValues = values || targets;
            const diff = targetValues.reduce((memo, thisArg, index) => {
                let modelValue = thisArg && thisArg.value || modelData[thisArg];

                if (Array.isArray(modelValue) && targetField) {
                    // The target is an array.  
                    // Perhaps we are calculating the sum of multiple entries in a data table

                    // Handle filtering the list by dependencies
                    if (dependencies) {
                        modelValue = getFilteredList({
                            modelValue,
                            dependencies,
                            name: thisArg
                        });
                    }

                    modelValue = modelValue.reduce((currentVal, thisItemValue, index) => {
                        const thisValue = toFloat(thisItemValue[targetField]);
                        
                        if (index === 0) {
                            return thisValue;
                        }
                        return currentVal - thisValue;
                    }, 0);
                }

                if (index === 0) {
                    // This is the first argument,
                    // We're just setting the value for the following to subtract from
                    return toFloat(modelValue);
                }

                return memo - modelValue;
            }, 0);

            return diff;
        },

        /* formula to divide a value from another question
            "formula": {
                "name": "divide",
                "arguments": {
                    "targets": [
                        "t12-revenue",
                        "total-assets"
                    ],
                    "values": [
                        "t12-revenue",
                        "total-assets"
                    ]
                }
            }
        */
        divide: function (args, modelData) {
            const { targets, targetField, dependencies, values } = args;

            const targetValues = values || targets;
            const diff = targetValues.reduce((memo, thisArg, index) => {
                let modelValue = thisArg && thisArg.value || modelData[thisArg];

                if (Array.isArray(modelValue) && targetField) {
                    // The target is an array.  
                    // Perhaps we are calculating the sum of multiple entries in a data table

                    // Handle filtering the list by dependencies
                    if (dependencies) {
                        modelValue = getFilteredList({
                            modelValue,
                            dependencies,
                            name: thisArg
                        });
                    }

                    modelValue = modelValue.reduce((currentVal, thisItemValue, index) => {
                        const thisValue = toFloat(thisItemValue[targetField]);
                        
                        if (index === 0) {
                            return thisValue;
                        }
                        return currentVal / thisValue;
                    }, 0);
                }

                if (index === 0) {
                    // This is the first argument,
                    // We're just setting the value for the following to subtract from
                    return toFloat(modelValue);
                }

                return memo / modelValue;
            }, 0);

            return diff;
        },

        /* formula to return content based on a condition
            targets will be evaluated in order and the first one that meets the criteria will be returned
            If a target has no dependencies, it will be returned

            "formula": "dynamicContent",
            "arguments": {
                "targets": [
                    Add the names of any fields here that should trigger this formula
                ]
                "values": [
                    {
                        "value": "result 1",
                        "dependencies": {
                            <dependencies object>
                        }
                    },
                    {
                        "value": "result 2",
                        "dependencies": {
                            <dependencies object>
                        }
                    },
                    {
                        "value": "default result",
                    },
                    {
                        "value": {
                            "formula": "join",
                            "arguments": {
                                "targets": [
                                    "first-name",
                                    "last-name"
                                ]
                            }
                        },
                        "dependencies": {
                            <dependencies object>
                        }
                    }
                ]
            }
        */
        dynamicContent: function (args, modelData) {
            const { values } = args;
            let value = '';

            values.some((thisArg) => {
                const { dependencies, value: thisValue } = thisArg;

                if (dependencies) {
                    const meetsDependencies = checkVisibility({
                        item: {
                            dependencies
                        },
                        model: modelData
                    });

                    if (meetsDependencies) {
                        if (thisValue && thisValue.formula) {
                            // value can contain an additional formula which will be used to generate the output
                            value = handlers.methods[thisValue.formula].call(this, thisValue.arguments, modelData);
                        } else {
                            // the value is static
                            value = thisValue;
                        }

                        return true;
                    }
                } else {
                    value = thisValue;

                    return true;
                }
            });

            return value;
        },

        /* formula to concatenate a string from values 
            "formula": "join",
            "arguments": {
                "targets": [
                    "first-name",
                    "last-name",
                    {
                        "value": "-"
                    },
                    "age"
                ],
                "delimiter": " "
            }
        */
        join: function (args, modelData) {
            const { targets, delimiter = ' ' } = args;

            const values = targets.map(thisArg => thisArg.value || modelData[thisArg]);

            return values.join(delimiter);
        },
        /* Create a sorted list of all values in an array-type value (data-table)
            "formula": "list",
            "arguments": {
                "delimiter": "\n",
                "deepValue": true,          // optional - will break questions out of an answer that is an array.
                                        // This can be used to create a unique list of answers from a multiselect checkbox
                                        // Note that you should not set "value" if this is true
                "sort": {
                    "target": "first",  // optional - if not provided, end values will be sorted
                    "order": "asc"
                },
                "targets": [
                    "employee-information"
                ],
                "unique": "true"        // optional - will only add unique values to the list
                "value": "full-name"    // optional - property to use as value if array items are objects
                "value": {              // optional - provide a sub-formula to calculate output values
                    "formula": {
                        "name": "join",
                        "arguments": {
                            "targets": [
                                "first-name",
                                "last-name"
                            ]
                        }
                    }
                }
            }
        */
        list: function (args, modelData) {
            // If argument is an array, will add up all entries and return a total count
            const self = this;
            const { deepValue, delimiter = '\n', sort, targets, unique, value } = args;

            // Get a list of all items to put in the list.
            // We are building a flat list in case there are multiple targets to combine into a single array
            const values = targets.reduce((memo, thisArg) => {
                const valueList = modelData[thisArg];

                if ( valueList ) {
                    if (!Array.isArray(valueList)) {
                        throw new Error('arguments in the "list" method must be an array');
                    }

                    return memo.concat(valueList);
                } else {
                    return memo
                }

            }, [])
            // Now it's time to calculate the value to include in the final list
            .reduce((memo, thisItem) => {
                // We are creating a list with both the item data and the calculated value so we can sort later on based on either value
                // {
                //    item: This is the full item data
                //    value: This is the calculated value that will be included in the final list
                // }
                const data = {
                    item: thisItem
                };

                if (value && value.formula) {
                    // value can contain an additional formula which will be used to generate the output
                    const { name } = value.formula;

                    const formulaValue = self[name].call(self, value.formula.arguments, thisItem);

                    data.value = formulaValue;
                } else if (value) {
                    // the value is a property name of the object - get the value for this key
                    data.value = thisItem[value];
                } else {
                    // the value is this item
                    data.value = thisItem;
                }

                // Break the value into individual answers if deepValue is true
                if (deepValue && Array.isArray(data.value)) {
                    memo.push(
                        ...data.value.map((thisValue) => ({
                            item: thisItem,
                            value: thisValue
                        }))
                    );
                } else {
                    memo.push(data);
                }

                return memo;
            }, []);

            // Sort by either the 
            if (sort) {
                const { target, order } = sort;
                const sortOrder = order === 'desc' ? -1 : 1;

                values.sort((itemA, itemB) => {
                    const valueA = target ? itemA.item[target] : itemA.value;
                    const valueB = target ? itemB.item[target] : itemB.value;
                    const sortValue = valueA > valueB ? 1 : valueB > valueA ? -1 : 0;

                    return sortValue * sortOrder;
                });
            }

            // Strip out the additional item data and check for duplicates if unique parameter is provided
            return values.reduce((memo, thisItem) => {
                // itemData is the data already in the list, if the unique parameter is provided
                // we don't want to include this if the "value" matches anything already in the list
                if (!unique || ( unique && !memo.some((itemData) => itemData.value === thisItem.value))) {
                    memo.push(thisItem);
                }

                return memo;
            }, [])
            .map((thisItem) => thisItem.value)
            .join(delimiter);
        },

        multiply: function (args, modelData) {
            const { targets, targetField, dependencies, values } = args;

            const targetValues = values || targets;

            const diff = targetValues.reduce((memo, thisArg, index) => {
                let modelValue = thisArg && thisArg.value || modelData[thisArg];

                if (Array.isArray(modelValue) && targetField) {
                    // The target is an array.  
                    // Perhaps we are calculating the sum of multiple entries in a data table

                    // Handle filtering the list by dependencies
                    if (dependencies) {
                        modelValue = getFilteredList({
                            modelValue,
                            dependencies,
                            name: thisArg
                        });
                    }

                    modelValue = modelValue.reduce((currentVal, thisItemValue, index) => {
                        const thisValue = toFloat(thisItemValue[targetField]);
                        
                        if (index === 0) {
                            return thisValue;
                        }
                        return currentVal * thisValue;
                    }, 0);
                }

                if (index === 0) {
                    // This is the first argument,
                    // We're just setting the value for the following to calculate from
                    return toFloat(modelValue);
                }

                return memo * modelValue;
            }, 0);

            return diff;
        },

        /* formula to return the first non-empty value from a list of fields
            "formula": "or",
            "arguments": {
                "targets": [
                    "employees"
                ]            
            }
        */
        or: function (args, modelData) {
            const { targets } = args;
            let value = '';

            targets.some((thisArg) => {
                // const modelValue = modelData[thisArg];
                const modelValue = modelData[thisArg] == 0 || modelData[thisArg] == '' ? '' : modelData[thisArg];

                if (modelValue) {
                    value = modelValue;
                }

                return modelValue;
            });

            return value;
        },

        /* formula to calculate the sum of multiple fields, including those with an array-type value (data-table)
            "formula": "sum",
            "arguments": {
                "targets": [
                    "employees"
                ],
                "dependencies": { // Only supported for data tables
                    "items": []
                    "operator": "and" 
                },
                "targetField": "salary" // Only supported for data tables               
            }
        */
        sum: function (args, modelData) {
            const { targets, targetField, dependencies, values } = args;
            const targetValues = values || targets;

            const sum = targetValues.reduce((memo, thisArg) => {
                let modelValue = thisArg && thisArg.value || modelData[thisArg];

                if (Array.isArray(modelValue) && targetField) {
                    // The target is an array.  
                    // Perhaps we are calculating the sum of multiple entries in a data table

                    // Handle filtering the list by dependencies
                    if (dependencies) {
                        modelValue = getFilteredList({
                            modelValue,
                            dependencies,
                            name: thisArg
                        });
                    }

                    modelValue = modelValue.reduce((currentSum, thisItemValue) => {
                        return currentSum + toFloat(thisItemValue[targetField]);
                    }, 0);
                } else {
                    modelValue = toFloat(modelValue);
                }

                return memo + modelValue;
            }, 0);
            
            return '' + sum;
        },
    }
};

export default handlers;


// WEBPACK FOOTER //
// ./src/components/questionnaire_template/mixins/formula-handler-mixins.js